/**
 * Push-Mode Action Gating — Phase 1: Surface Intelligence
 * ───────────────────────────────────────────────────────
 * Pure helper. Given an issue (and optionally its job), returns which
 * Push-Mode surfaces logically apply, plus context-aware labels.
 *
 * Rule: hide entirely when not relevant. No collapsed escape hatch.
 * The whole point is to make Push Mode feel intentional, not optional.
 *
 * This is presentation gating only — it does NOT change the underlying
 * Next Action Brain / suggestedActions / nextStepEngine. Those continue
 * to drive what happens next.
 */
import type { Issue, Job } from "../types";

export interface PushAvailableActions {
  /** Show the InlineSchedulerBlock (book a visit)? */
  showScheduler: boolean;
  /** Label for the scheduler trigger — context-aware. */
  schedulerLabel: string;
  /** Helper text for the scheduler trigger. */
  schedulerSubLabel: string;
  /** Show the suggested-vendor outreach card? (sourcing only) */
  showVendorContacts: boolean;
  /** Show the contractor / crew outreach card? */
  showContractorContacts: boolean;
  /** Label for the contractor block — context-aware. */
  contractorBlockLabel: string;
  /**
   * Outreach-Only mode — the resolution path IS the call/email.
   * When true, the modal hides the manual "New status" picker; status
   * is inferred from the outreach the operator actually performed.
   * This eliminates a redundant click and a class of human error
   * (status saying one thing while the call dispo says another).
   */
  isOutreachOnly: boolean;
  /**
   * Status to auto-assign on Confirm when isOutreachOnly is true.
   *  - "In Progress" once any outreach (call / email / log) has been recorded
   *  - "Open" if Confirm fires with no outreach logged (we did nothing)
   * Resolution still requires the operator to explicitly resolve later
   * (after the dispo confirms the issue is actually closed out).
   */
  autoStatusWithOutreach: "In Progress";
  autoStatusWithoutOutreach: "Open";
}

const SOURCING_DESC_RX = /discontinued|sourc|out of stock|backorder|vendor|supplier/i;
const SCHED_DESC_RX = /reschedul|conflict|crew unavail|no.show|delay/i;
const ACCESS_DESC_RX = /access|gate|key|lockbox|on.?site|home.?owner unavail/i;
const SERVICE_DESC_RX = /damage|leak|repair|warranty|defect|callback/i;
const PERMIT_DESC_RX = /permit|inspection|municipal|jurisdiction/i;

export function getAvailableActions(
  issue: Issue | null | undefined,
  _job?: Job | undefined,
): PushAvailableActions {
  // Safe defaults — when we don't know, we hide the heavier surfaces
  // rather than show noise.
  if (!issue) {
    return {
      showScheduler: false,
      schedulerLabel: "Schedule a visit",
      schedulerSubLabel: "Book a visit related to this job",
      showVendorContacts: false,
      showContractorContacts: false,
      contractorBlockLabel: "Contractor contacts",
      isOutreachOnly: false,
      autoStatusWithOutreach: "In Progress",
      autoStatusWithoutOutreach: "Open",
    };
  }

  const desc = issue.description ?? "";
  const type = issue.issueType ?? "";
  const cat = issue.blockerCategory ?? "";
  const area = issue.affectedArea ?? "";

  // ── Classification ──
  const isSourcing =
    type === "Material Issue" ||
    cat === "Missing Material" ||
    cat === "Damaged Material" ||
    cat === "Supplier Delay" ||
    area === "Materials" ||
    SOURCING_DESC_RX.test(desc);

  const isScheduling =
    type === "Scheduling Issue" ||
    area === "Scheduling" ||
    cat === "Scheduling Conflict" ||
    SCHED_DESC_RX.test(desc);

  const isContractor =
    type === "Contractor Issue" || !!issue.relatedContractorId;

  const isAccess = ACCESS_DESC_RX.test(desc);
  const isService =
    type === "Customer Complaint" || type === "Install Issue" || SERVICE_DESC_RX.test(desc);
  const isPermit = type === "Permit Issue" || PERMIT_DESC_RX.test(desc);

  // ── Scheduler gating: only show when a physical visit logically helps. ──
  // Material sourcing / permit-paperwork / billing issues do NOT need a visit.
  let showScheduler = false;
  let schedulerLabel = "Schedule a visit";
  let schedulerSubLabel = "Book a visit related to this job";

  if (isService) {
    showScheduler = true;
    schedulerLabel = "Schedule service call";
    schedulerSubLabel = "Send a tech out to address this issue";
  } else if (isAccess) {
    showScheduler = true;
    schedulerLabel = "Schedule pre-install check";
    schedulerSubLabel = "Confirm site access before the install";
  } else if (isScheduling || isContractor) {
    showScheduler = true;
    schedulerLabel = "Reschedule install";
    schedulerSubLabel = "Move the install to a workable date";
  } else if (isPermit) {
    // Permit issues are paperwork-driven — don't push a visit.
    showScheduler = false;
  } else if (isSourcing) {
    // Sourcing issues are vendor-driven — no visit makes sense yet.
    showScheduler = false;
  } else {
    // Unknown / generic operational issue — keep it quiet.
    showScheduler = false;
  }

  // ── Contact card gating ──
  const showVendorContacts = isSourcing;
  const showContractorContacts = isScheduling || isContractor;

  let contractorBlockLabel = "Contractor contacts";
  if (isScheduling) contractorBlockLabel = "Crews to reassign";
  else if (isContractor) contractorBlockLabel = "Assigned crew & backups";

  // ── Outreach-Only gating ──
  // The "right" resolution for these scenarios is to call/email the right
  // person. The dispo of that outreach IS the new status — so we hide
  // the status picker and let the action drive the state.
  // We are deliberately conservative: only outreach-shaped scenarios
  // (scheduling / contractor / sourcing / access) qualify. Service issues
  // and permit issues still require an explicit status decision because
  // they involve work, not just contact.
  const isOutreachOnly =
    issue.status !== "Resolved" &&
    issue.status !== "Closed" &&
    (isScheduling || isContractor || isSourcing || isAccess) &&
    !isService;

  return {
    showScheduler,
    schedulerLabel,
    schedulerSubLabel,
    showVendorContacts,
    showContractorContacts,
    contractorBlockLabel,
    isOutreachOnly,
    autoStatusWithOutreach: "In Progress",
    autoStatusWithoutOutreach: "Open",
  };
}
